// SPDX-License-Identifier: AGPL-3.0-or-later
// Budget amounts for sub-categories. A main category's slider sets its total;
// a split divides that total among its children, keeping a line for the
// category itself ("everything else") so the parts always add up.
import { rebalance, splitByWeight, type BudgetLine, type Cents } from "@almanac/domain";
import { MAIN_CATEGORIES, mainOf, migrateCategory, parentOf, wasRenamed } from "./categories.js";
import type { BudgetPlan } from "./model.js";

/** Brings a plan saved before sub-categories existed up to date. Returns the same object if nothing changed. */
export function migratePlan(plan: BudgetPlan): BudgetPlan {
  const old = plan as BudgetPlan & { splits?: BudgetPlan["splits"] };
  const needsWork = !old.splits || plan.lines.some((l) => wasRenamed(l.id)) || MAIN_CATEGORIES.some((id) => !plan.lines.some((l) => l.id === id));
  if (!needsWork) return plan;
  const amounts = new Map<string, Cents>();
  const moved = new Map<string, Cents>();
  const locked = new Set<string>();
  for (const line of plan.lines) {
    const id = migrateCategory(line.id);
    const main = mainOf(id);
    amounts.set(main, (amounts.get(main) ?? 0) + line.amount);
    if (id !== main) moved.set(id, (moved.get(id) ?? 0) + line.amount);
    if (line.locked) locked.add(main);
  }
  const lines = MAIN_CATEGORIES.map((id) => ({ id, amount: amounts.get(id) ?? 0, locked: locked.has(id) }));
  const splits: BudgetPlan["splits"] = { ...(old.splits ?? {}) };
  // Money that used to be a main category of its own becomes that sub-category's share.
  for (const [id, amount] of moved) {
    const parent = parentOf(id)!;
    const existing = splits[parent] ?? [{ id: parent, amount: (amounts.get(parent) ?? 0) - amount, locked: false }];
    splits[parent] = [...existing.filter((l) => l.id !== id), { id, amount, locked: false }];
  }
  return { ...plan, lines, splits };
}

/** The budget for one category at any level. */
export function amountOf(plan: BudgetPlan, id: string): Cents {
  const parent = parentOf(id);
  if (!parent) return plan.lines.find((l) => l.id === id)?.amount ?? 0;
  const split = plan.splits[parent];
  if (!split) return 0;
  return split.find((l) => l.id === id)?.amount ?? 0;
}

/**
 * The split for a category with children: a line per child plus one for the
 * category itself, adding up to the category's amount. Children without a
 * line yet start at $0; any difference lands on the category's own line.
 */
export function splitFor(plan: BudgetPlan, id: string, children: readonly string[]): BudgetLine[] {
  const total = amountOf(plan, id);
  const saved = plan.splits[id] ?? [];
  const lines = [id, ...children].map((c) => ({ ...(saved.find((l) => l.id === c) ?? { id: c, amount: 0, locked: false }) }));
  const own = lines[0]!;
  own.amount += total - lines.reduce((s, l) => s + l.amount, 0);
  if (own.amount < 0) {
    // The category shrank below what its children held: scale the children down to fit.
    return scale(lines, total);
  }
  return lines;
}

/** Scales lines to a new total, keeping their proportions to the cent. */
function scale(lines: readonly BudgetLine[], total: Cents): BudgetLine[] {
  const weights = lines.map((l) => Math.max(0, l.amount));
  const parts = weights.some((w) => w > 0) ? splitByWeight(total, weights) : lines.map((_, i) => (i === 0 ? total : 0));
  return lines.map((l, i) => ({ ...l, amount: parts[i] ?? 0 }));
}

/**
 * Fits lines to a new total. A locked line holds what it shows: a dollar
 * amount stays put, a percentage keeps its share of the total. Unlocked lines
 * share what is left in proportion to their size; if they are all at $0, the
 * line at `spill` takes it. When the locked lines no longer fit, or nothing is
 * unlocked, everything scales together.
 */
export function retotal(lines: readonly BudgetLine[], from: Cents, to: Cents, spill: number): BudgetLine[] {
  const held = lines.map((l) => (!l.locked ? null : l.unit === "percent" && from > 0 ? Math.round((l.amount * to) / from) : l.amount));
  const free = lines.flatMap((l, i) => (held[i] === null ? [i] : []));
  const rest = to - held.reduce<number>((s, h) => s + (h ?? 0), 0);
  if (free.length === 0 || rest < 0) return scale(lines, to);
  const weights = free.map((i) => Math.max(0, lines[i]!.amount));
  const at = free.includes(spill) ? spill : free.at(-1)!;
  const parts = weights.some((w) => w > 0) ? splitByWeight(rest, weights) : free.map((i) => (i === at ? rest : 0));
  return lines.map((l, i) => ({ ...l, amount: held[i] ?? parts[free.indexOf(i)] ?? 0 }));
}

/** Changes the monthly pool, fitting the main lines to it as `retotal` does. */
export function setPool(plan: BudgetPlan, pool: Cents): BudgetPlan {
  return setLines({ ...plan, pool }, retotal(plan.lines, plan.pool, pool, plan.lines.length - 1));
}

/** Shows every line, main and sub-category, in one unit. */
export function setAllUnits(plan: BudgetPlan, unit: "dollars" | "percent"): BudgetPlan {
  const apply = (lines: BudgetLine[]) => lines.map((l) => ({ ...l, unit }));
  return { ...plan, lines: apply(plan.lines), splits: Object.fromEntries(Object.entries(plan.splits).map(([k, v]) => [k, apply(v)])) };
}

/** Replaces a category's split and keeps every split below it in step with the new amounts. */
export function setSplit(plan: BudgetPlan, id: string, lines: BudgetLine[]): BudgetPlan {
  let next: BudgetPlan = { ...plan, splits: { ...plan.splits, [id]: lines } };
  for (const line of lines) if (line.id !== id) next = resize(next, line.id, line.amount);
  return next;
}

/** After a category's amount changes, scales its split (and theirs, all the way down). */
export function resize(plan: BudgetPlan, id: string, total: Cents): BudgetPlan {
  const split = plan.splits[id];
  if (!split) return plan;
  const current = split.reduce((s, l) => s + l.amount, 0);
  if (current === total) return plan;
  const lines = retotal(split, current, total, 0);
  let next: BudgetPlan = { ...plan, splits: { ...plan.splits, [id]: lines } };
  for (const line of lines) if (line.id !== id) next = resize(next, line.id, line.amount);
  return next;
}

/** Applies new main-category lines and resizes any split whose total moved. */
export function setLines(plan: BudgetPlan, lines: BudgetLine[]): BudgetPlan {
  let next: BudgetPlan = { ...plan, lines };
  for (const line of lines) next = resize(next, line.id, line.amount);
  return next;
}

/**
 * Spreads a split's total in proportion to what each part actually cost
 * recently. Locked lines keep their amounts. Returns null when there is no
 * spending to go by.
 */
export function splitByActuals(lines: readonly BudgetLine[], spent: (id: string) => Cents): BudgetLine[] | null {
  const free = lines.filter((l) => !l.locked);
  const weights = free.map((l) => Math.max(0, spent(l.id)));
  if (!weights.some((w) => w > 0)) return null;
  const total = free.reduce((s, l) => s + l.amount, 0);
  const parts = splitByWeight(total, weights);
  let i = 0;
  return lines.map((l) => (l.locked ? { ...l } : { ...l, amount: parts[i++] ?? 0 }));
}

/**
 * Holds the large-purchases line at `amount` and locked, so the other sliders
 * work around it. The difference comes from (or goes back to) the unlocked
 * lines in proportion to their size, whatever the slider mode, so small
 * categories are not emptied; if they cannot cover it, the line gets what fits. Returns the same
 * plan when nothing needs to change.
 */
export function reservePurchases(plan: BudgetPlan, amount: Cents): BudgetPlan {
  const line = plan.lines.find((l) => l.id === "purchases");
  if (!line || (line.amount === amount && line.locked)) return plan;
  const open = plan.lines.map((l) => (l.id === "purchases" ? { ...l, locked: false } : l));
  const { lines } = rebalance(open, "purchases", amount, "proportional");
  const next = lines.map((l) => (l.id === "purchases" ? { ...l, locked: true } : l));
  if (next.every((l, i) => l.amount === plan.lines[i]!.amount && l.locked === plan.lines[i]!.locked)) return plan;
  return setLines(plan, next);
}
